/**
 * 表示文字列。
 *
 * **ゲームのルールは 1 つも入れない。** ここにあるのは見せ方だけで、
 * `src/sim` は言語を知らない。だから翻訳を足しても挙動は 1 ビットも変わらず、
 * `SIM_VERSION` にも影響しない。日本語と英語の記録を同じ表で競わせられる。
 *
 * チュートリアルは**文字をいっさい使わない**設計なので、ここには何も無い。
 * 縮む輪が閉じた瞬間にタップする、それだけで通じる。
 */

export type Lang = 'ja' | 'en';

const LANG_KEY = 'etw:lang';

/**
 * 表示する言語。
 *
 * 端末の設定を既定にする。**最初の 1 画面で言語を選ばせない。**
 * URL を踏んで 3 秒で泳ぎ出せることが本作の獲得の前提なので、
 * 選択画面を挟むとその前提が崩れる。手で変えたい人にはメニューに切り替えを置く。
 */
function detect(): Lang {
  try {
    const saved = localStorage.getItem(LANG_KEY);
    if (saved === 'ja' || saved === 'en') return saved;
  } catch {
    // プライベートブラウズ。端末の設定に従う。
  }
  try {
    return navigator.language.toLowerCase().startsWith('ja') ? 'ja' : 'en';
  } catch {
    return 'en';
  }
}

let current: Lang = detect();

export function lang(): Lang {
  return current;
}

export function setLang(next: Lang): void {
  current = next;
  try {
    localStorage.setItem(LANG_KEY, next);
  } catch {
    // 保存できなくても、この起動のあいだは切り替わる。
  }
}

/** 数値の桁区切り。英語だけ 1,234 にする。 */
function num(value: number): string {
  return current === 'en' ? value.toLocaleString('en-US') : String(value);
}

interface Strings {
  // ── メニュー ──
  tagline: string;
  modeNormal: string;
  modeDaily: string;
  modeKids: string;
  boardFastest: string;
  boardTreasure: string;
  boardOverall: string;
  noRecord: string;
  offshore: (m: number) => string;
  bigWaves: string;
  lowTide: string;
  strongRip: string;
  traitSeparator: string;
  bestLine: (time: string, total: number) => string;
  depart: (stage: string) => string;
  noteDaily: (date: string) => string;
  noteKids: string;
  noteNormal: (runs: number) => string;
  transferCode: (code: string) => string;
  enterCode: string;
  installHint: string;

  // ── 走行中 ──
  toShore: (m: number) => string;
  combo: (n: number) => string;
  rodeWave: (n: number) => string;
  dolphin: (n: number) => string;
  jellyfish: string;
  dolphinRide: string;
  wiped: (points: number) => string;
  wipedNoLoss: string;
  hatchlings: (n: number) => string;
  breaking: string;
  muted: string;
  /** 初回だけ、その場で出す手引き。**要るときに 1 つずつ出す。** */
  cueSteer: string;
  cueRide: string;
  cueDive: string;
  caughtSafe: string;
  // ── あそびかた ──
  howTo: string;
  howToTitle: string;
  howToLead: string;
  howToRide: { title: string; body: string };
  howToCrest: { title: string; body: string };
  howToFoam: { title: string; body: string };
  howToTreasure: { title: string; body: string };
  howToHatchling: { title: string; body: string };
  howToDolphin: { title: string; body: string };
  howToJelly: { title: string; body: string };
  howToColorGood: string;
  howToColorBad: string;
  back: string;

  /** 初めて出会ったときだけ、それが何なのかを言う。 */
  firstTreasure: string;
  firstHatchling: string;
  firstDolphin: string;
  firstJellyfish: string;

  // ── 開始前 ──
  youAreTurtle: string;
  dailySameSea: string;
  fromOffshore: (m: number) => string;
  howSteer: string;
  howDive: (seconds: string) => string;
  goal: (m: number) => string;
  startHint: string;
  startHintGhost: string;
  start: string;
  toMenu: string;

  // ── 一時停止 ──
  paused: string;
  resume: string;
  restart: string;

  // ── 結果 ──
  finished: string;
  timeMultiplier: (x: string) => string;
  rowFinish: string;
  rowTreasure: (got: number, max: number) => string;
  rowHatchlings: (n: number) => string;
  rowCombo: (n: number) => string;
  rowTotal: string;
  points: (n: number) => string;
  ranks: (fastest: string, treasure: string, overall: string) => string;
  rank: (n: number) => string;
  noRank: string;
  again: string;
  share: string;
  shareCopied: string;
  shareFailed: string;
  /** 共有文。**URL は別に渡す**ので、ここには入れない。 */
  shareText: (stage: string, time: string, total: number, daily: boolean) => string;

  // ── 引き継ぎコード ──
  codeTitle: string;
  codeLead: string;
  codeMemo: string;
  codeNoServer: string;
  codeEnterLabel: string;
  codeSubmit: string;
  codeClose: string;
  codeChecking: string;
  codeRestored: (n: number) => string;
  codeRestoredEmpty: string;
  codeStaleServer: string;
  codeBad: string;
  codeNotFound: string;
  codeOffline: string;
  codeNoServerRestore: string;
}

const JA: Strings = {
  tagline: 'アオウミガメになって、生まれた浜へ帰る',
  modeNormal: '自由に走る',
  modeDaily: '今日の海',
  modeKids: 'キッズ',
  boardFastest: '最速タイム',
  boardTreasure: '宝ポイント',
  boardOverall: '総合',
  noRecord: 'まだ記録なし',
  offshore: (m) => `沖 ${m} m`,
  bigWaves: '大波',
  lowTide: '干潮あり',
  strongRip: '離岸流が強い',
  traitSeparator: ' ・ ',
  bestLine: (time, total) => `最速 ${time}\n総合 ${num(total)} pt`,
  depart: (stage) => `${stage} へ出発`,
  noteDaily: (date) => `今日の海は全員同じ配置。日付が変われば別の海になる。\n（${date} UTC）`,
  noteKids: '波が弱く、クラゲも離岸流もない。\n記録はこの端末に残る（みんなのランキングには載らない）。',
  noteNormal: (runs) => `走行 ${runs} 回。前回の最高記録がゴーストで並走する。`,
  transferCode: (code) => `引き継ぎコード  ${code}`,
  enterCode: '引き継ぎコードを入れる',
  installHint: 'ホーム画面に追加すると、次に開くまで日が空いても記録が残ります',

  toShore: (m) => `浜まで あと ${m} m`,
  combo: (n) => `COMBO ×${n}`,
  rodeWave: (n) => `波に乗った ×${n}`,
  dolphin: (n) => `ドルフィンスルー ×${n}`,
  jellyfish: 'クラゲ！',
  dolphinRide: 'イルカに掴まった',
  wiped: (points) => `巻かれた  −${num(points)} pt`,
  wipedNoLoss: '巻かれた',
  hatchlings: (n) => `子ガメ ${n} 匹`,
  breaking: '崩れる！ いま潜れ',
  muted: 'ミュート中',
  cueSteer: '指を置くと、その方へ泳ぐ',
  cueRide: 'この面に乗ると速い',
  cueDive: '輪が閉じたらタップ',
  caughtSafe: '巻かれても平気。遅くなるだけ',
  howTo: 'あそびかた',
  howToTitle: 'あそびかた',
  howToLead: '見分けがつけば、あとは指 1 本。',
  howToRide: { title: '乗れる波', body: '緑色の帯に入ると加速する。長く乗るほどコンボが伸びる。' },
  howToCrest: { title: 'もうすぐ崩れる波', body: '峰にオレンジ色の線が出たら合図。崩れる直前まで乗って、タップして潜る。' },
  howToFoam: { title: '崩れの先頭', body: 'オレンジ色で縁取られたここだけが危ない。潜っていれば通り抜けられる。当たっても遅くなるだけ。' },
  howToTreasure: { title: '宝', body: '拾うと点になる。大きいものほど高い。' },
  howToHatchling: { title: '子ガメ', body: '触れると付いてくる。連れて帰った数だけ点になる。' },
  howToDolphin: { title: 'イルカ', body: '触れると浜の方へ引いてくれる。' },
  howToJelly: { title: 'クラゲ', body: '触れると遅くなる。それだけで、終わりはしない。' },
  howToColorGood: '緑色 … 味方。乗れる、拾える',
  howToColorBad: 'オレンジ色 … 危ない。当たると遅くなるだけ',
  back: 'もどる',
  firstTreasure: '宝。拾うと点になる',
  firstHatchling: '子ガメ。連れて帰ると点になる',
  firstDolphin: 'イルカ。触れると引いてくれる',
  firstJellyfish: 'クラゲ。遅くなるだけで、終わりはしない',

  youAreTurtle: 'あなたはアオウミガメ。',
  dailySameSea: '今日の海。全員が同じ配置を走る。',
  fromOffshore: (m) => `沖 ${m} m から、生まれた浜へ帰る。`,
  howSteer: '指を置く → その位置へ泳ぐ',
  howDive: (seconds) => `タップ → 潜る（${seconds} 秒）`,
  goal: (m) => `沖 ${m} m。上の浜まで泳いで帰るだけ。`,
  startHint: '画面のどこでもタップして開始\nキーボード: ← → / Space / R',
  startHintGhost: '自己ベストのゴーストが並走します\n画面のどこでもタップして開始',
  start: 'はじめる',
  toMenu: 'メニューへ',

  paused: '一時停止',
  resume: '続ける',
  restart: 'やり直す',

  finished: '帰還',
  timeMultiplier: (x) => `タイム倍率 ×${x}`,
  rowFinish: '帰還',
  rowTreasure: (got, max) => `宝  ${num(got)} / ${num(max)}`,
  rowHatchlings: (n) => `子ガメ ${n} 匹`,
  rowCombo: (n) => `コンボ 最大 ${n}`,
  rowTotal: '総合',
  points: (n) => `${num(n)} pt`,
  ranks: (fastest, treasure, overall) =>
    `最速 ${fastest}　宝 ${treasure}　総合 ${overall}`,
  rank: (n) => `${n} 位`,
  noRank: '—',
  again: 'もう1回',
  share: '共有',
  shareCopied: 'コピーしました',
  shareFailed: 'コピーできませんでした',
  shareText: (stage, time, total, daily) =>
    daily
      ? `今日の海（${stage}）を ${time} で帰還、${num(total)} pt。同じ海で勝てる？ #EscapeTheWaves`
      : `${stage} を ${time} で帰還、${num(total)} pt。#EscapeTheWaves`,

  codeTitle: '引き継ぎコード',
  codeLead:
    '別の端末でこのコードを入れると、記録を引き継げます。登録は要りません。\n' +
    '戻せるのは、サーバーに届いた走行（最後まで帰還したもの）だけです。',
  codeMemo: 'メモしておいてください。端末のデータが消えると、これが無いと戻せません。',
  codeNoServer:
    'サーバーに繋がっていないので、まだコードは発行されていません。オンラインで一度開くと発行されます。',
  codeEnterLabel: 'コードを入れて引き継ぐ',
  codeSubmit: '引き継ぐ',
  codeClose: '閉じる',
  codeChecking: '照会中…',
  codeRestored: (n) => `引き継ぎました。記録を ${n} 件戻しました。`,
  codeRestoredEmpty: '引き継ぎました。このコードにはまだ記録がありません。',
  codeStaleServer: '引き継ぎましたが、サーバーが古いため記録を戻せませんでした。',
  codeBad: 'コードは 8 文字です。O・0・I・1・L は使いません。読み違いを確かめてください。',
  codeNotFound: 'そのコードは見つかりませんでした。',
  codeOffline: '繋がりませんでした。電波のあるところで試してください。',
  codeNoServerRestore: 'サーバーに繋がっていないので引き継げません。',
};

/**
 * 英語。
 *
 * **訳ではなく、同じことを英語で言い直す。** とくに説明文は、
 * 日本語の語順をなぞると長くなって画面に収まらない。短さを優先する。
 */
const EN: Strings = {
  tagline: 'Swim home to the beach where you hatched',
  modeNormal: 'Free run',
  modeDaily: "Today's sea",
  modeKids: 'Kids',
  boardFastest: 'Fastest time',
  boardTreasure: 'Treasure',
  boardOverall: 'Overall',
  noRecord: 'No runs yet',
  offshore: (m) => `${m} m out`,
  bigWaves: 'big waves',
  lowTide: 'low tide',
  strongRip: 'strong rips',
  traitSeparator: ' · ',
  bestLine: (time, total) => `Best ${time}\nTotal ${num(total)} pt`,
  depart: (stage) => `Swim ${stage}`,
  noteDaily: (date) => `Same sea for everyone today. A new one at midnight.\n(${date} UTC)`,
  noteKids: 'Gentle waves, no jellyfish, no rips.\nKept on this device, not in the shared ranking.',
  noteNormal: (runs) => `${runs} run${runs === 1 ? '' : 's'}. Your best run swims beside you.`,
  transferCode: (code) => `Transfer code  ${code}`,
  enterCode: 'Enter a transfer code',
  installHint: 'Add to your home screen and your records survive between visits',

  toShore: (m) => `${m} m to shore`,
  combo: (n) => `COMBO ×${n}`,
  rodeWave: (n) => `Rode the wave ×${n}`,
  dolphin: (n) => `Dolphin dive ×${n}`,
  jellyfish: 'Jellyfish!',
  dolphinRide: 'Caught a dolphin',
  wiped: (points) => `Wiped out  −${num(points)} pt`,
  wipedNoLoss: 'Wiped out',
  hatchlings: (n) => `${n} hatchling${n === 1 ? '' : 's'}`,
  breaking: 'Breaking! Dive now',
  muted: 'Muted',
  cueSteer: 'Hold anywhere — you swim there',
  cueRide: 'Ride this face to go faster',
  cueDive: 'Tap when the ring closes',
  caughtSafe: 'Getting caught is fine — it only costs time',
  howTo: 'How to play',
  howToTitle: 'How to play',
  howToLead: 'Once you can tell them apart, it is one finger.',
  howToRide: { title: 'A wave you can ride', body: 'Enter the teal band to speed up. The longer you ride, the higher the combo.' },
  howToCrest: { title: 'About to break', body: 'An orange line on the crest is the warning. Ride until the last moment, then tap to dive.' },
  howToFoam: { title: 'The breaking front', body: 'Only this orange-edged strip hurts. Dive and you pass under it. Getting hit only costs time.' },
  howToTreasure: { title: 'Treasure', body: 'Pick it up for points. Bigger is worth more.' },
  howToHatchling: { title: 'Hatchling', body: 'Touch it and it follows you. Every one you bring home is worth points.' },
  howToDolphin: { title: 'Dolphin', body: 'Touch it and it tows you towards the beach.' },
  howToJelly: { title: 'Jellyfish', body: 'Touching it slows you down. That is all it does.' },
  howToColorGood: 'Teal … on your side. Ride it, collect it',
  howToColorBad: 'Orange … it hurts. Only ever costs you time',
  back: 'Back',
  firstTreasure: 'Treasure. Pick it up for points',
  firstHatchling: 'A hatchling. Bring it home for points',
  firstDolphin: 'A dolphin. Touch it for a tow',
  firstJellyfish: 'A jellyfish. It slows you, nothing more',

  youAreTurtle: 'You are a green sea turtle.',
  dailySameSea: "Today's sea. Everyone swims the same one.",
  fromOffshore: (m) => `${m} m out. Swim home to the beach where you hatched.`,
  howSteer: 'Hold → swim to your finger',
  howDive: (seconds) => `Tap → dive (${seconds}s)`,
  goal: (m) => `${m} m out. Just swim to the beach at the top.`,
  startHint: 'Tap anywhere to start\nKeys: ← → / Space / R',
  startHintGhost: 'Your best run swims beside you\nTap anywhere to start',
  start: 'Start',
  toMenu: 'Menu',

  paused: 'Paused',
  resume: 'Resume',
  restart: 'Restart',

  finished: 'Home',
  timeMultiplier: (x) => `Time multiplier ×${x}`,
  rowFinish: 'Made it home',
  rowTreasure: (got, max) => `Treasure  ${num(got)} / ${num(max)}`,
  rowHatchlings: (n) => `Hatchlings ${n}`,
  rowCombo: (n) => `Best combo ${n}`,
  rowTotal: 'Total',
  points: (n) => `${num(n)} pt`,
  ranks: (fastest, treasure, overall) =>
    `Time ${fastest}   Treasure ${treasure}   Overall ${overall}`,
  rank: (n) => `#${n}`,
  noRank: '—',
  again: 'Again',
  share: 'Share',
  shareCopied: 'Copied',
  shareFailed: 'Could not copy',
  shareText: (stage, time, total, daily) =>
    daily
      ? `Made it home from today's sea (${stage}) in ${time} for ${num(total)} pt. Same sea for everyone — beat it? #EscapeTheWaves`
      : `Made it home from ${stage} in ${time} for ${num(total)} pt. #EscapeTheWaves`,

  codeTitle: 'Transfer code',
  codeLead:
    'Enter this code on another device to bring your records with you. No sign-up.\n' +
    'Only runs that reached the server (ones you finished) can come back.',
  codeMemo: 'Write it down. If this device loses its data, this is the only way back.',
  codeNoServer: 'Not connected, so no code has been issued yet. Open the game online once.',
  codeEnterLabel: 'Enter a code',
  codeSubmit: 'Transfer',
  codeClose: 'Close',
  codeChecking: 'Checking…',
  codeRestored: (n) => `Done. Brought back ${n} record${n === 1 ? '' : 's'}.`,
  codeRestoredEmpty: 'Done. This code has no records yet.',
  codeStaleServer: 'Transferred, but the server is out of date so records could not come back.',
  codeBad: 'Codes are 8 characters and never use O, 0, I, 1 or L. Check for a misread character.',
  codeNotFound: 'That code was not found.',
  codeOffline: 'Could not reach the server. Try again with a connection.',
  codeNoServerRestore: 'Not connected, so nothing can be transferred.',
};

/** 文字列を引く。`t().start` のように使う。 */
export function t(): Strings {
  return current === 'ja' ? JA : EN;
}

/**
 * ステージ名。
 *
 * `src/sim` には日本語名しか無いので、id で引き直す。
 * **sim に表示都合を持ち込まない**ための遠回りで、これは意図的。
 */
const STAGE_NAMES: Record<string, Record<Lang, string>> = {
  waikiki: { ja: 'ワイキキ', en: 'Waikiki' },
  northshore: { ja: 'ノースショア', en: 'North Shore' },
  uluwatu: { ja: 'ウルワツ', en: 'Uluwatu' },
};

export function stageName(id: string, fallback: string): string {
  // キッズモードは id に接尾辞が付く。名前は同じ海のものを使う。
  const base = id.replace(/-kids$/, '');
  return STAGE_NAMES[base]?.[current] ?? fallback;
}
